import { motion } from "framer-motion";
import { useLanguage } from "../components/LanguageContext";
import { useState, useEffect } from "react";

// Backend API URL.
//
// Production: empty string → fetch URLs are relative ("/api/contact/submit"),
// hitting the same origin (z-creative-partners.com), where the CF Worker
// (cloudflare-worker/src/index.js) handles /api/* itself.
//
// Local dev: set VITE_API_URL=http://localhost:8000 (or wherever your local
// backend runs) in .env.local to override.
const API_URL = import.meta.env.VITE_API_URL || '';

export default function ContactSection() {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    subject: '',
    message: '',
    newsletter: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error' | 'duplicate'>('idle');
  const [submitMessage, setSubmitMessage] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState<'idle' | 'success' | 'error' | 'duplicate'>('idle');
  const [newsletterMessage, setNewsletterMessage] = useState('');
  
  // Debug: Log state changes
  useEffect(() => {
    console.log('Newsletter status changed to:', newsletterStatus);
  }, [newsletterStatus]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Client-side validation
    if (!formData.name.trim() || !formData.email.trim() || !formData.subject.trim()) {
      setSubmitStatus('error');
      console.error('Please fill in all required fields: name, email, and subject');
      setTimeout(() => setSubmitStatus('idle'), 5000);
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Collect visitor data for analytics
      const visitorData = {
        browserLanguage: navigator.language,
        allLanguages: navigator.languages?.join(','),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        referrer: document.referrer,
        currentPage: window.location.pathname,
        userAgent: navigator.userAgent,
        platform: navigator.platform
      };

      const response = await fetch(`${API_URL}/api/contact/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          visitorData,
          consent: true // User consented by submitting form
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSubmitStatus('success');
        
        // Clear form immediately
        setFormData(prev => ({
          ...prev,
          name: '',
          email: '',
          company: '',
          phone: '',
          subject: '', // Clear subject too
          message: '',
        }));
        
        // Show success message for 7 seconds
        setTimeout(() => setSubmitStatus('idle'), 7000);
      } else if (result.isDuplicate) {
        // Handle duplicate contact with friendly message
        setSubmitStatus('duplicate');
        setSubmitMessage(result.message);
        console.log('Contact duplicate:', result.message);
        setTimeout(() => setSubmitStatus('idle'), 10000); // Show for 10 seconds
      } else {
        setSubmitStatus('error');
        setSubmitMessage(result.message || 'Failed to send message. Please try again.');
        console.error('Contact form error:', result.message);
        // Show error message for 5 seconds
        setTimeout(() => setSubmitStatus('idle'), 5000);
      }
    } catch (error) {
      console.error('Contact form submission error:', error);
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus('idle'), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.newsletter.trim()) {
      setNewsletterStatus('error');
      setTimeout(() => setNewsletterStatus('idle'), 5000);
      return;
    }
    
    setIsSubmitting(true);
    setNewsletterStatus('idle');

    try {
      // Collect visitor data for analytics
      const visitorData = {
        browserLanguage: navigator.language,
        allLanguages: navigator.languages?.join(','),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        referrer: document.referrer,
        currentPage: window.location.pathname,
        userAgent: navigator.userAgent,
        platform: navigator.platform
      };

      const response = await fetch(`${API_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.newsletter,
          visitorData,
          consent: true // User consented by subscribing
        })
      });

      const result = await response.json();
      
      console.log('Newsletter Response:', { ok: response.ok, result });

      if (response.ok && result.success) {
        console.log('Setting newsletter status to success');
        setNewsletterStatus('success');
        
        // Clear newsletter email immediately
        setFormData(prev => ({ ...prev, newsletter: '' }));
        
        // Show success message for 7 seconds
        setTimeout(() => {
          console.log('Clearing success message after 7 seconds');
          setNewsletterStatus('idle');
        }, 7000);
      } else if (result.isDuplicate) {
        // Handle duplicate subscription with friendly message
        setNewsletterStatus('duplicate');
        setNewsletterMessage(result.message);
        console.log('Newsletter duplicate:', result.message);
        setTimeout(() => setNewsletterStatus('idle'), 10000); // Show for 10 seconds
      } else {
        setNewsletterStatus('error');
        setNewsletterMessage(result.message || 'Failed to subscribe. Please try again.');
        console.error('Newsletter subscription error:', result.message);
        setTimeout(() => setNewsletterStatus('idle'), 5000);
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      setNewsletterStatus('error');
      setTimeout(() => setNewsletterStatus('idle'), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-left space-y-6 mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl lg:text-5xl text-foreground">
            {t('contact.contactUs')}
          </h2>
        </motion.div>

        <div className="space-y-12">
          {/* Global Offices - Compact List Design */}
          <div className="grid lg:grid-cols-2 gap-8">
            <motion.div
              className="bg-white border border-slate-200/60 rounded-xl p-8 hover:shadow-xl transition-all duration-300 hover:border-cyan-400/30"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.02 }}
            >
              <h3 className="text-2xl font-semibold text-foreground mb-6">
                {t('contact.globalOffices')}
              </h3>
              
              <div className="space-y-5">
                {/* Japan Office */}
                <div className="flex gap-3">
                  <span className="text-xl mt-1">📍</span>
                  <div>
                    <h4 className="font-medium text-foreground mb-1">
                      {t('contact.japanOffice')}
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      {t('contact.japanAddress')}
                    </p>
                  </div>
                </div>
                
                {/* Thailand Office */}
                <div className="flex gap-3">
                  <span className="text-xl mt-1">📍</span>
                  <div>
                    <h4 className="font-medium text-foreground mb-1">
                      {t('contact.thailandOffice')}
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      {t('contact.thailandAddress')}
                    </p>
                  </div>
                </div>

                {/* Vietnam Office */}
                <div className="flex gap-3">
                  <span className="text-xl mt-1">📍</span>
                  <div>
                    <h4 className="font-medium text-foreground mb-1">
                      {t('contact.vietnamOffice')}
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      {t('contact.vietnamAddress')}
                    </p>
                  </div>
                </div>

                {/* China Office */}
                <div className="flex gap-3">
                  <span className="text-xl mt-1">📍</span>
                  <div>
                    <h4 className="font-medium text-foreground mb-1">
                      {t('contact.chinaOffice')}
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      {t('contact.chinaAddress')}
                    </p>
                  </div>
                </div>

                {/* Email Section */}
                <div className="pt-4 mt-4 border-t border-slate-200">
                  <div className="flex gap-3 items-center">
                    <span className="text-xl">✉️</span>
                    <div>
                      <h4 className="font-medium text-foreground mb-1">
                        {t('contact.emailLabel')}
                      </h4>
                      <a 
                        href="mailto:info@z-creative-partners.com" 
                        className="text-sm text-cyan-600 hover:text-cyan-700 transition-colors"
                      >
                        info@z-creative-partners.com
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Newsletter - moved to second column */}
            <motion.div 
              className="bg-white border border-slate-200/60 rounded-xl p-8 hover:shadow-xl transition-all duration-300 hover:border-cyan-400/30"
              whileHover={{ scale: 1.02 }}
            >
              <h3 className="text-3xl text-foreground mb-6 text-center">
                {t('contact.subscribe')} <span className="text-cyan-400">{t('contact.newsletter')}</span>
              </h3>
              <p className="text-muted-foreground text-center mb-8">
                {t('contact.newsletterDesc')}
              </p>
              <form onSubmit={handleNewsletterSubmit} className="space-y-4">
                <input
                  type="email"
                  placeholder={t('contact.enterYourEmail')}
                  value={formData.newsletter}
                  onChange={(e) => setFormData({...formData, newsletter: e.target.value})}
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400/20"
                  required
                  aria-label={t('forms.ariaEmailNewsletter')}
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-slate-800 text-white px-6 py-3 rounded-lg hover:bg-slate-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                >
                  {isSubmitting ? t('contact.subscribing') : t('contact.subscribeButton')}
                </button>
              </form>
              
              {/* Newsletter Status Messages */}
              {newsletterStatus === 'success' && (
                <motion.div 
                  className="mt-4 p-4 bg-green-50 border-2 border-green-300 rounded-lg shadow-md"
                  initial={{ opacity: 0, y: -10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ duration: 0.4, type: "spring" }}
                >
                  <p className="text-green-700 text-base text-center font-semibold">
                    ✅ Successfully subscribed!
                  </p>
                  <p className="text-green-600 text-sm text-center mt-1">
                    Welcome email sent to your inbox.
                  </p>
                </motion.div>
              )}
              {newsletterStatus === 'duplicate' && (
                <motion.div 
                  className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <p className="text-blue-700 text-sm text-center">
                    ℹ️ {newsletterMessage}
                  </p>
                </motion.div>
              )}
              {newsletterStatus === 'error' && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-700 text-sm text-center">
                    ✗ {newsletterMessage || 'Failed to subscribe. Please try again or contact us directly.'}
                  </p>
                </div>
              )}
            </motion.div>
          </div>

          {/* Contact Form - Full Width */}
          <motion.div 
            className="bg-white border border-slate-200/60 rounded-xl p-8 hover:shadow-xl transition-all duration-300 hover:border-cyan-400/30"
            whileHover={{ scale: 1.02 }}
          >
              <h3 className="text-3xl text-foreground mb-8 text-center">
                {t('contact.contactUs')}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-foreground text-sm mb-2">
                    {t('contact.name')} <span className="text-destructive">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-0 py-3 border-0 border-b border-slate-300 focus:outline-none focus:border-slate-800"
                    required
                    aria-label={t('forms.ariaYourName')}
                  />
                </div>

                <div>
                  <label className="block text-foreground text-sm mb-2">
                    {t('contact.email')} <span className="text-destructive">*</span>
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-0 py-3 border-0 border-b border-slate-300 focus:outline-none focus:border-slate-800"
                    required
                    aria-label={t('forms.ariaYourEmail')}
                  />
                </div>

                <div>
                  <label className="block text-foreground text-sm mb-2">
                    {t('contact.company')} {t('contact.optional')}
                  </label>
                  <input
                    type="text"
                    value={formData.company}
                    onChange={(e) => setFormData({...formData, company: e.target.value})}
                    className="w-full px-0 py-3 border-0 border-b border-slate-300 focus:outline-none focus:border-slate-800"
                    aria-label={t('contact.company')}
                  />
                </div>

                <div>
                  <label className="block text-foreground text-sm mb-2">
                    {t('contact.phone')} {t('contact.optional')}
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full px-0 py-3 border-0 border-b border-slate-300 focus:outline-none focus:border-slate-800"
                    aria-label={t('contact.phone')}
                  />
                </div>

                <div>
                  <label className="block text-foreground text-sm mb-2">
                    {t('contact.subject')} <span className="text-destructive">*</span>
                  </label>
                  <select
                    value={formData.subject}
                    onChange={(e) => setFormData({...formData, subject: e.target.value})}
                    className="w-full px-0 py-3 border-0 border-b border-slate-300 focus:outline-none focus:border-slate-800 cursor-pointer"
                    required
                    aria-label={t('forms.ariaMessageSubject')}
                  >
                    <option value="">{t('forms.selectSubject')}</option>
                    <option value="geo-solutions">{t('forms.geoSolutionInquiry')}</option>
                    <option value="sns-o2o-marketing">{t('forms.socialMediaInquiry')}</option>
                    <option value="social-commerce-ec">{t('forms.socialCommerceInquiry')}</option>
                    <option value="search-ads">{t('forms.searchAdsInquiry')}</option>
                    <option value="market-insights">{t('forms.newsInsightsInquiry')}</option>
                    <option value="partnership">{t('forms.partnershipInquiry')}</option>
                    <option value="general">{t('forms.generalInquiry')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-foreground text-sm mb-2">
                    {t('contact.message')} {t('contact.optional')}
                  </label>
                  <textarea
                    rows={3}
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    className="w-full px-0 py-3 border-0 border-b border-slate-300 focus:outline-none focus:border-slate-800 resize-none"
                    aria-label={t('forms.ariaYourMessage')}
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full border-2 border-cyan-400 text-cyan-400 px-8 py-3 rounded-lg hover:bg-cyan-400 hover:text-white transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                >
                  {isSubmitting ? 'Sending...' : t('contact.sendEmail')}
                </button>
                
                {/* Contact Form Status Messages */}
                {submitStatus === 'success' && (
                  <motion.div 
                    className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <p className="text-green-700 text-center font-medium">
                      ✓ Message sent successfully! We'll get back to you within 24-48 hours.
                    </p>
                    <p className="text-green-600 text-center text-sm mt-1">
                      Check your email for a confirmation copy.
                    </p>
                  </motion.div>
                )}
                {submitStatus === 'duplicate' && (
                  <motion.div 
                    className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <p className="text-blue-700 text-center">
                      ℹ️ {submitMessage}
                    </p>
                  </motion.div>
                )}
                {submitStatus === 'error' && (
                  <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-700 text-center">
                      ✗ {submitMessage || 'Please fill in all required fields (Name, Email, and Subject) before submitting.'}
                    </p>
                  </div>
                )}
              </form>
            </motion.div>
        </div>
      </div>
    </section>
  );
}